// Static catalogue of the scrapers the admin console can inspect.
//
// Deliberately hard-coded rather than DB-backed: a scraper only exists if
// there is code implementing it, so the list is a property of the build, not
// of the data. Run *history* is the dynamic part and lives in `scraperRuns`.

/**
 * How a job gets started.
 *  - `scheduled` — runs itself on a timer, no user involvement.
 *  - `once-per-enterprise` — fired a single time when someone signs up and
 *    picks their agency. Never repeats for that enterprise.
 */
export type ScraperJobCadence = 'scheduled' | 'once-per-enterprise';

export interface ScraperJobDefinition {
  id: string;
  name: string;
  cadence: ScraperJobCadence;
  description: string;
  /** Counter keys this job reports, in display order. */
  metrics: string[];
}

export interface ScraperDefinition {
  id: string;
  name: string;
  /** Human label for where the data comes from. */
  source: string;
  sourceUrl: string;
  region: string;
  description: string;
  jobs: ScraperJobDefinition[];
}

export const SCRAPERS: ScraperDefinition[] = [
  {
    id: 'property-finder',
    name: 'Property Finder',
    source: 'propertyfinder.ae',
    sourceUrl: 'https://www.propertyfinder.ae',
    region: 'UAE',
    description:
      'Broker directory, agency profiles, agents and listings from Property Finder UAE.',
    jobs: [
      {
        id: 'agencies',
        name: 'Agency directory',
        cadence: 'scheduled',
        description:
          'Walks the full broker directory and refreshes the master list of agencies. Runs on its own schedule — no user action involved.',
        metrics: ['pages', 'agencies', 'new', 'updated'],
      },
      {
        id: 'agency',
        name: 'Agency import',
        cadence: 'once-per-enterprise',
        description:
          'Fired once when a user signs up and selects their agency. Pulls that agency’s profile, agents and listings, then never runs again for that enterprise.',
        metrics: ['agents', 'listings', 'images', 'pages'],
      },
    ],
  },
];

export function findScraper(id: string): ScraperDefinition | undefined {
  return SCRAPERS.find((s) => s.id === id);
}

export type ScraperRunStatus = 'running' | 'success' | 'partial' | 'failed';
export type ScraperRunTrigger = 'schedule' | 'signup' | 'manual';

export interface ScraperRunItem {
  _id: string;
  scraperId: string;
  jobId: string;
  status: ScraperRunStatus;
  trigger: ScraperRunTrigger;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number;
  /** Set for once-per-enterprise runs; null for the scheduled directory job. */
  enterprise: string | null;
  enterpriseName: string | null;
  /** Which source-side agency this run covered, when applicable. */
  target: {
    name: string | null;
    externalId: string | null;
    url: string | null;
  } | null;
  counts: Record<string, number>;
  /** Free-form run metadata (proxy mode, credits, page range, …). */
  meta: Record<string, unknown>;
  error: string | null;
}

export interface ScraperJobSummary {
  jobId: string;
  total: number;
  lastRunAt: string | null;
  lastStatus: ScraperRunStatus | null;
  /** Distinct enterprises covered — only meaningful for per-agency jobs. */
  enterprises: number;
  counts: Record<string, number>;
}

export interface ScraperRunListResponse {
  items: ScraperRunItem[];
  meta: { total: number; page: number; pages: number; limit: number };
  summary: ScraperJobSummary[];
}
